import { useContext } from 'react';
import { TenantContext } from '../../../context/tenant-context';
import { handleRequest } from '../quipli-service-client';
import {
  CreatePaymentIntentRequest,
  CreatePaymentRequest,
  CreateRefundRequest,
  CreateStatementRefundRequest,
  StripeBankAccount,
} from './payment-v2';
import { CheckedPaymentResponse, Payment, PaymentRefundRequirement, StripeCard, StripePayment } from './payment';
import { PaymentParsers } from './payment-parsers';
import { Stripe } from 'stripe';

export const usePaymentApiV2 = () => {
  const { tenant, contextPath } = useContext(TenantContext);

  async function createPayment(request: CreatePaymentRequest): Promise<Payment> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v2/payments`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };
    const payment = await handleRequest<PaymentParsers.PaymentJson>(config);
    return PaymentParsers.toPayment(payment);
  }

  async function refundInvoice(invoiceId: string, request: CreateRefundRequest): Promise<Payment[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v2/payments/${invoiceId}/refund`,
      headers: {
        'X-Tenant': tenant,
      },
      data: { ...request, method: request.method.replaceAll(' ', '') },
    };
    const payments = await handleRequest<PaymentParsers.PaymentJson[]>(config);
    return payments.map(PaymentParsers.toPayment);
  }

  async function refundRequirements(
    invoiceId: string,
    request: CreateRefundRequest,
  ): Promise<PaymentRefundRequirement[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v2/payments/${invoiceId}/refund/requirements`,
      headers: {
        'X-Tenant': tenant,
      },
      data: { ...request, method: request.method.replaceAll(' ', '') },
    };
    return await handleRequest<PaymentRefundRequirement[]>(config);
  }

  async function initiateTerminalRefund(
    terminalId: string,
    invoiceId: string,
    request: CreateRefundRequest,
  ): Promise<void> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v2/payments/terminal/${terminalId}/${invoiceId}/refund`,
      headers: {
        'X-Tenant': tenant,
      },
      data: { ...request, method: request.method.replaceAll(' ', '') },
    };
    return await handleRequest<void>(config);
  }

  async function refundStatement(userId: number, request: CreateStatementRefundRequest): Promise<Payment> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v2/payments/${userId}/statement/refund`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };
    const payment = await handleRequest<PaymentParsers.PaymentJson>(config);
    return PaymentParsers.toPayment(payment);
  }

  async function captureCard(request: { confirmationTokenId: string; userId: number }): Promise<StripeCard> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v2/payments/methods/card`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };
    return await handleRequest<StripeCard>(config);
  }

  async function cancelTerminalAction(terminalId: string): Promise<void> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v2/payments/terminal/cancel/${terminalId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    await handleRequest<void>(config);
  }

  async function addBankAccount(params: { userId: number; stripePaymentMethodId: string; accountHolderName: string }) {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v2/payments/methods/bank`,
      headers: {
        'X-Tenant': tenant,
      },
      data: params,
    };
    return await handleRequest<StripeBankAccount>(config);
  }

  async function searchBankAccounts(params: { userIds?: number[]; stripePaymentMethodIds?: string[]; ids?: string[] }) {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v2/payments/methods/bank/search`,
      headers: {
        'X-Tenant': tenant,
      },
      data: params,
    };
    return await handleRequest<StripeBankAccount[]>(config);
  }

  async function deleteBankAccount(bankAccountId: string, userId: number) {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/v2/payments/methods/bank/${userId}/${bankAccountId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest<void>(config);
  }

  async function getReaderStatus(readerId: string): Promise<Stripe.Terminal.Reader> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v2/payments/terminal/status/${readerId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest<Stripe.Terminal.Reader>(config);
  }

  async function createPaymentIntent(request: CreatePaymentIntentRequest): Promise<StripePayment> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v2/payments/createIntent`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };
    return await handleRequest<StripePayment>(config);
  }

  async function unapplyPayment(invoiceId: string, request: CreateRefundRequest): Promise<Payment[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v2/payments/${invoiceId}/unapply`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };
    const payments = await handleRequest<PaymentParsers.PaymentJson[]>(config);
    return payments.map(PaymentParsers.toPayment);
  }

  async function checkInvoicePaymentStatus(invoiceId: string): Promise<CheckedPaymentResponse> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v2/payments/${invoiceId}/status`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const payments = await handleRequest<PaymentParsers.CheckedPaymentJson>(config);
    return PaymentParsers.toCheckedPaymentResponse(payments);
  }

  return {
    refundInvoice,
    createPayment,
    createPaymentIntent,
    cancelTerminalAction,
    getReaderStatus,
    searchBankAccounts,
    addBankAccount,
    deleteBankAccount,
    captureCard,
    refundStatement,
    unapplyPayment,
    checkInvoicePaymentStatus,
    refundRequirements,
    initiateTerminalRefund,
  };
};
